import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { STATES } from "@/constants/states";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["High-speed", "Low-speed", "Electric", "Surgical", "Other"];

const empty = {
  practice_name: "", first_name: "", last_name: "", email: "", phone: "",
  street: "", unit: "", city: "", state: "", zip: "",
  handpiece_type: "High-speed", handpiece_count: 1, brand_model: "", problem: "", notes: "",
  pkg_weight: "", pkg_dims: "", delivery_method: "email", terms: false, website: "",
};

const field = (name, placeholder, extra = {}) => ({ name, placeholder, ...extra });

function Section({ n, title, children }) {
  return (
    <div>
      <p className="eyebrow text-brandsky-deep">{`${n} · ${title}`}</p>
      <div className="mt-3.5 grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export default function LabelRequestForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    for (const [k, msg] of [
      ["practice_name", "Practice name is required"], ["first_name", "First name is required"],
      ["last_name", "Last name is required"], ["email", "Email is required"],
      ["phone", "Phone is required"], ["street", "Street address is required"],
      ["city", "City is required"], ["state", "State is required"], ["zip", "ZIP code is required"],
    ]) {
      if (!String(form[k]).trim()) {
        toast.error(msg);
        return false;
      }
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
      toast.error("Please enter a valid 5-digit ZIP code");
      return false;
    }
    if (!form.terms) {
      toast.error("Please confirm the information is accurate and agree to the terms");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/label-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          handpiece_count: parseInt(form.handpiece_count || "1", 10),
          pkg_weight: form.pkg_weight ? parseFloat(form.pkg_weight) : null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.detail === "string" ? j.detail : "Something went wrong — please call us at (716) 256-6630.");
      }
      const data = await res.json();
      navigate(`/label-request-thank-you?ref=${encodeURIComponent(data.request_id)}`);
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-[24px] border border-ink/10 bg-white p-6 shadow-[0_20px_60px_rgba(22,50,79,0.12)] sm:p-9" data-testid="label-request-form">
      <Section n="1" title="Dental office information">
        <input className="field" placeholder="Dental practice name *" value={form.practice_name} onChange={set("practice_name")} data-testid="label-input-practice" />
        <input className="field" placeholder="Contact first name *" value={form.first_name} onChange={set("first_name")} data-testid="label-input-first-name" />
        <input className="field" placeholder="Contact last name *" value={form.last_name} onChange={set("last_name")} data-testid="label-input-last-name" />
        <input className="field" type="email" placeholder="Email address *" value={form.email} onChange={set("email")} data-testid="label-input-email" />
        <input className="field" type="tel" placeholder="Phone number *" value={form.phone} onChange={set("phone")} data-testid="label-input-phone" />
      </Section>

      <Section n="2" title="Shipping information">
        <input className="field" placeholder="Street address *" value={form.street} onChange={set("street")} data-testid="label-input-street" />
        <input className="field" placeholder="Suite or unit (optional)" value={form.unit} onChange={set("unit")} data-testid="label-input-unit" />
        <input className="field" placeholder="City *" value={form.city} onChange={set("city")} data-testid="label-input-city" />
        <select className="field" value={form.state} onChange={set("state")} data-testid="label-input-state">
          <option value="">State *</option>
          {STATES.map(([ab, name]) => <option key={ab} value={ab}>{name}</option>)}
        </select>
        <input className="field" placeholder="ZIP code *" value={form.zip} onChange={set("zip")} data-testid="label-input-zip" />
      </Section>

      <Section n="3" title="Repair information">
        <select className="field" value={form.handpiece_type} onChange={set("handpiece_type")} data-testid="label-input-type">
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className="field" type="number" min="1" max="50" placeholder="Number of handpieces *" value={form.handpiece_count} onChange={set("handpiece_count")} data-testid="label-input-count" />
        <input className="field" placeholder="Brand / model (optional)" value={form.brand_model} onChange={set("brand_model")} data-testid="label-input-brand" />
        <input className="field" placeholder="Description of problem (optional)" value={form.problem} onChange={set("problem")} data-testid="label-input-problem" />
        <textarea className="field sm:col-span-2 min-h-[80px] resize-y" placeholder="Additional notes — and to claim your 15% first-order discount, write a note (e.g. “15% off first order”)" value={form.notes} onChange={set("notes")} data-testid="label-input-notes" />
      </Section>

      <Section n="4" title="Package & label delivery">
        <input className="field" type="number" step="0.1" min="0.1" max="70" placeholder="Estimated package weight, lb (optional)" value={form.pkg_weight} onChange={set("pkg_weight")} data-testid="label-input-weight" />
        <input className="field" placeholder="Package dimensions, in (optional, e.g. 9 x 6 x 4)" value={form.pkg_dims} onChange={set("pkg_dims")} data-testid="label-input-dims" />
        <select className="field sm:col-span-2" value={form.delivery_method} onChange={set("delivery_method")} data-testid="label-input-delivery">
          <option value="email">Email the label to me</option>
          <option value="download">I'll download it from the confirmation page</option>
        </select>
      </Section>

      <label className="mt-6 flex items-start gap-3 rounded-2xl border border-ink/10 bg-paper p-4">
        <input type="checkbox" checked={form.terms} onChange={(e) => setForm((f) => ({ ...f, terms: e.target.checked }))} className="mt-0.5 h-5 w-5 shrink-0 accent-[#16324F]" data-testid="label-input-terms" />
        <span className="text-[13px] leading-relaxed text-slate-600">
          I confirm this information is accurate and my office agrees to the shipping and repair terms. I understand
          JSL Dental Repair reviews every request before issuing a prepaid label.
        </span>
      </label>

      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={() => {}} />

      <button
        type="submit"
        disabled={busy}
        data-testid="label-submit-button"
        className="btn-lift mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-ink px-8 py-4 text-[15px] font-bold text-white shadow-[0_16px_36px_rgba(22,50,79,0.3)] hover:bg-abyss disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {busy ? "Submitting…" : "Request my prepaid shipping label"}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[12px] text-slate-400">
        <CheckCircle2 className="h-3.5 w-3.5 text-brandleaf-deep" />
        Reviewed by a human before any label is issued · no purchase happens at this step
      </p>
    </form>
  );
}